import React, { useContext } from 'react';
import { StringColumn, CustomColumn, CategoricalColumn } from 'baseui/data-table';
import { useStyletron } from 'baseui';
import _toLower from 'lodash/toLower';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _startsWith from 'lodash/startsWith';
import { Button, KIND } from 'baseui/button';
import { StyledLink } from 'baseui/link';
import dayjs from 'dayjs';
import * as lodash from 'lodash';

import { CustomDateColumn, DrawerContext, EMPTY_OBJECT, EMPTY_STRING, GLOBAL_THEME } from 'commons-ui';
import { UserDetailsContext } from 'context/userDetails';
import { GAME_NAMES, GAME_NAME_OPTIONS } from 'pages/constants';
import TransactionDetails from '../transactionDetails/index';

export const COL_NAMES = {
	transactionId: 'tid',
	transactionType: 'transactionType',
	amount: 'amount',
	credit: 'credit',
	debit: 'debit',
	status: 'status',
	transactionTimestamp: 'timestamp',
	bonus: 'bonus',
	winnings: 'winnings',
	deposit: 'deposit',
	extras: 'extras',
	tournamentId: 'tournamentId',
	cgrId: 'cgrId',
	type: 'type',
	message: 'message',
	cashback: 'cashback',
	gstTransactionId: 'gstTransactionId',
	depositTxnId: 'depositTxnId',
	gstAmount: 'gstAmount',
	pgAmount: 'pgAmount'
};

export const columnConfigs = [
	CustomColumn({
		title: 'Transaction Id',
		maxWidth: 300,
		mapDataToValue: (data: any) => {
			return {
				transactionId: data[COL_NAMES.transactionId] || 0,
				transactionType: data[COL_NAMES.transactionType],
				status: data[COL_NAMES.status],
				transactionTimestamp: dayjs(new Date(data[COL_NAMES.transactionTimestamp])).format('DD-MM-YYYY hh:mm A'),
				tournamentId: _get(data, [COL_NAMES.tournamentId]),
				cgrId: _get(data, [COL_NAMES.cgrId]),
				bonus: data[COL_NAMES.bonus],
				deposit: data[COL_NAMES.deposit],
				amount: data[COL_NAMES.amount],
				winnings: data[COL_NAMES.winnings],
				extras: data[COL_NAMES.extras] || EMPTY_OBJECT,
				type: data[COL_NAMES.type],
				message: data[COL_NAMES.message],
				cashback: data[COL_NAMES.cashback],
				gstTransactionId: data[COL_NAMES.gstTransactionId],
				depositTxnId: data[COL_NAMES.depositTxnId],
				gstAmount: data[COL_NAMES.gstAmount],
				pgAmount: data[COL_NAMES.pgAmount]
			};
		},
		renderCell: (params: any) => {
			const { value } = params;

			const { setDrawerConfig } = useContext(DrawerContext);
			const handleOnClick = (event: any) => {
				event.preventDefault();
				event.stopPropagation();
				setDrawerConfig({ isOpen: true, content: <TransactionDetails transactionAllDetails={value} /> });
			};

			return (
				<Button
					kind={KIND.tertiary}
					onClick={handleOnClick}
					overrides={{
						Root: {
							style: ({ $theme }) => ({
								color: $theme.colors.primaryA,
								height: '100%',
								width: '100%',
								fontSize: '12px',
								fontWeight: 'normal'
							})
						}
					}}>
					<StyledLink>{value.transactionId}</StyledLink>
				</Button>
			);
		},
		textQueryFilter: (textQuery: String, data: any): boolean => {
			return _startsWith(data.transactionId, `${textQuery}`);
		}
	}),
	CategoricalColumn({
		title: 'Transaction Type',
		maxWidth: 200,
		mapDataToValue: (data: any) => data[COL_NAMES.transactionType]
	}),
	CustomColumn({
		title: 'Tournament Id',
		maxWidth: 200,
		mapDataToValue: (data: any) => {
			return {
				transactionId: (data[COL_NAMES.tournamentId] || EMPTY_STRING).toString(),
				gameName: _get(GAME_NAME_OPTIONS, _get(data, [COL_NAMES.extras, 'gameName'], GAME_NAMES.LUDO))
			};
		},
		renderCell: (params: any) => {
			const { value } = params;
			const {
				selectedUser: { uid }
			} = useContext(UserDetailsContext);

			const handleOnClick = (event: any) => {
				event.preventDefault();
				event.stopPropagation();
				if (_isEmpty(value)) return;
				const url = `/userProfile/gameplay/details?uid=${uid}&ltid=${value?.transactionId}&gameName=${value?.gameName}`;
				window.open(url, '_blank');
			};

			return (
				<Button
					kind={KIND.tertiary}
					onClick={handleOnClick}
					overrides={{
						Root: {
							style: ({ $theme }) => ({
								color: $theme.colors.primaryA,
								height: '100%',
								width: '100%',
								fontSize: '12px',
								fontWeight: 'normal'
							})
						}
					}}>
					<StyledLink>{value?.transactionId}</StyledLink>
				</Button>
			);
		},
		textQueryFilter: (textQuery: String, data: any): boolean => {
			return _startsWith(data, `${textQuery}`);
		}
	}),
	StringColumn({
		title: 'Credit (₹)',
		maxWidth: 200,
		mapDataToValue: (data: any) => (data[COL_NAMES.credit] || '').toString()
	}),
	StringColumn({
		title: 'Debit (₹)',
		maxWidth: 200,
		mapDataToValue: (data: any) => (data[COL_NAMES.debit] || '').toString()
	}),
	CustomColumn({
		title: 'Status',
		mapDataToValue: (data: any) => data[COL_NAMES.status],
		maxWidth: 200,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			let backgroundColor = GLOBAL_THEME.Colors.white;
			const lowerCaseValue = _toLower(value);

			if (lowerCaseValue === 'complete') {
				backgroundColor = GLOBAL_THEME.Colors.cellGreen;
			} else if (lowerCaseValue === 'pending') {
				backgroundColor = GLOBAL_THEME.Colors.cellOrange;
			} else if (lowerCaseValue === 'charged') {
				backgroundColor = GLOBAL_THEME.Colors.cellRed;
			}
			return (
				<div
					className={css({
						backgroundColor,
						height: '100%',
						width: '100%',
						paddingLeft: '10px'
					})}>
					{value}
				</div>
			);
		}
	}),
	CustomDateColumn({
		title: 'Transaction Timestamp',
		maxWidth: 300,
		columnName: COL_NAMES.transactionTimestamp,
		format: 'YYYY-MM-DD hh:mm A'
	}),
	StringColumn({
		title: 'GST Txn ID',
		maxWidth: 500,
		mapDataToValue: (data: any) => {
			return lodash.isNil(data[COL_NAMES.gstTransactionId]) ? EMPTY_STRING : data[COL_NAMES.gstTransactionId].toString();
		}
	})
];

export const DEFAULT_CONFIG = {
	pageDetails: {
		pageNo: 1,
		pageSize: 100
	}
};
